import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@roger/ui-components';

import {
  closePatientSyncReviewPatientsPatientIdChartImportsSyncReviewClosePost as closePatientSyncReview,
  getPatientSyncReviewPatientsPatientIdChartImportsSyncReviewGet as getPatientSyncReview,
} from '../../services/api/sdk.gen';
import type { ChartSyncReviewResponse } from '../../services/api/types.gen';
import { useStore } from '../../store';
import SyncReviewDialog from './SyncReviewDialog';
import {
  SYNC_REVIEW_CLOSE_ERROR_COPY,
  syncReviewButtonLabel,
} from './syncReviewCopy';

interface SyncReviewButtonProps {
  patientId: string;
}

/**
 * The way into the sync review, beside the import report.
 *
 * It stands on the chart once a refresh has applied to it after the move.
 * It is loud while the review holds changes nobody has closed, and quiet
 * once someone opens it and closes it (Daniel, 2026-10-12: "its loudness
 * being dismissed once opened and closed"). Closing clears it for the whole
 * agency, so the dot follows the chart, not the reader.
 *
 * The close marks only the runs the dialog showed. A run that lands while
 * the dialog is open stays new.
 */
const SyncReviewButton: React.FC<SyncReviewButtonProps> = ({ patientId }) => {
  const showError = useStore(state => state.showError);
  const [review, setReview] = useState<ChartSyncReviewResponse | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await getPatientSyncReview({
          path: { patient_id: patientId },
          throwOnError: true,
        });
        if (!cancelled) setReview(data ?? null);
      } catch (err) {
        // Silent by design, like the import report control: an unreadable
        // review leaves the chart unchanged and shows no control.
        console.error('Failed to load the sync review', err);
        if (!cancelled) setReview(null);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next || !review) return;
      const runIds = review.run_ids ?? [];
      if (runIds.length === 0) return;
      void (async () => {
        try {
          const { data } = await closePatientSyncReview({
            path: { patient_id: patientId },
            body: { run_ids: runIds },
            throwOnError: true,
          });
          if (data) setReview(data);
        } catch (err) {
          console.error('Failed to close the sync review', err);
          showError(SYNC_REVIEW_CLOSE_ERROR_COPY);
        }
      })();
    },
    [patientId, review, showError]
  );

  if (!review?.has_sync_runs) return null;

  // The backend's count of the review's lines: each change, each added
  // record, each nightly Action finding. The Patient Manager card shows the
  // same number.
  const newCount = review.new_count ?? 0;
  const loud = newCount > 0;

  return (
    <>
      <Button
        variant={loud ? 'default' : 'outline'}
        size='sm'
        onClick={() => handleOpenChange(true)}
        data-testid='sync-review-button'
        data-loud={loud ? 'true' : 'false'}
        className='relative'
      >
        <RefreshCw className='h-3.5 w-3.5 mr-1.5' aria-hidden='true' />
        <span className='whitespace-nowrap'>
          {syncReviewButtonLabel(newCount)}
        </span>
        {loud && (
          <span
            className='absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-background'
            aria-hidden='true'
            data-testid='sync-review-dot'
          />
        )}
      </Button>
      <SyncReviewDialog
        review={review}
        open={open}
        onOpenChange={handleOpenChange}
      />
    </>
  );
};

export default SyncReviewButton;
